import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate, uploadReceipt, receiptHref } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Paperclip, Repeat, Truck } from "lucide-react";

const CATEGORIES = ["Rent", "Utilities", "Insurance", "Tools & Equipment", "Shop Supplies", "Wages", "Subscriptions", "Marketing", "Other"];
const monthStart = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses({ embedded = false }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("oneoff");
  const [range, setRange] = useState({ start: monthStart(), end: today() });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: expenses = [] } = useQuery({
    queryKey: ["mech-expenses", range],
    queryFn: async () => (await api.get("/expenses", { params: range })).data,
  });
  const { data: allExpenses = [] } = useQuery({
    queryKey: ["mech-expenses-all"],
    queryFn: async () => (await api.get("/expenses")).data,
  });
  const { data: suppliersOverview = [] } = useQuery({
    queryKey: ["mech-suppliers-overview"],
    queryFn: async () => (await api.get("/suppliers-overview")).data,
    enabled: tab === "suppliers",
  });

  const oneOff = useMemo(() => expenses.filter((e) => !e.recurring), [expenses]);
  const recurring = useMemo(() => allExpenses.filter((e) => e.recurring), [allExpenses]);

  const total = oneOff.reduce((s, e) => s + Number(e.amount || 0), 0);
  const monthlyRecurring = recurring.reduce((s, e) => s + Number(e.amount || 0), 0);
  const byCat = oneOff.reduce((m, e) => { m[e.category] = (m[e.category] || 0) + Number(e.amount || 0); return m; }, {});
  const totalSupplierSpend = suppliersOverview.reduce((s, r) => s + Number(r.total_spend || 0), 0);

  const openDlg = (item, defaultRecurring = false) => {
    setEditing(item);
    setForm(item
      ? { ...item }
      : { date: today(), category: "Other", description: "", vendor: "", amount: "", notes: "", recurring: defaultRecurring, recurring_frequency: "monthly" });
    setReceipt(item?.receipt_url ? { url: item.receipt_url, name: item.receipt_name } : null);
    setOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        amount: Number(form.amount || 0),
        recurring: !!form.recurring,
        recurring_frequency: form.recurring_frequency || "monthly",
        receipt_url: receipt?.url || "",
        receipt_name: receipt?.name || "",
      };
      if (editing) return api.put(`/expenses/${editing.id}`, body);
      return api.post("/expenses", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-expenses"] });
      qc.invalidateQueries({ queryKey: ["mech-expenses-all"] });
      toast.success("Saved");
      setOpen(false);
    },
    onError: () => toast.error("Save failed"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-expenses"] });
      qc.invalidateQueries({ queryKey: ["mech-expenses-all"] });
      toast.success("Deleted");
    },
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const r = await uploadReceipt(file); setReceipt({ url: r.url, name: r.name || file.name }); toast.success("Receipt attached"); }
    catch { toast.error("Upload failed"); }
    setBusy(false); e.target.value = "";
  };

  return (
    <div className={embedded ? "" : "p-8"} data-testid="mech-expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          {!embedded && (
            <>
              <h1 className="font-heading text-3xl mb-1">Expenses</h1>
              <p className="text-muted-foreground">One-off shop expenses, recurring monthly bills, and supplier spend all in one place.</p>
            </>
          )}
        </div>
        {tab === "oneoff" && (
          <div className="flex items-end gap-2">
            <div><Label className="label-mono">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="exp-start" /></div>
            <div><Label className="label-mono">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="exp-end" /></div>
            <Button onClick={() => openDlg(null, false)} data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1" />Add expense</Button>
          </div>
        )}
        {tab === "recurring" && (
          <Button onClick={() => openDlg(null, true)} data-testid="add-recurring-btn"><Plus className="h-4 w-4 mr-1" />Add recurring</Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="oneoff" data-testid="tab-oneoff">One-off expenses</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="h-3.5 w-3.5 mr-1" />Recurring · {money(monthlyRecurring)}/mo</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers"><Truck className="h-3.5 w-3.5 mr-1" />Supplier spend</TabsTrigger>
        </TabsList>

        <TabsContent value="oneoff">
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Card className="p-5">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Total in range</p>
              <p className="text-2xl font-heading font-bold" data-testid="exp-total">{money(total)}</p>
            </Card>
            <Card className="p-5 sm:col-span-2">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">By category</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([c, v]) => (
                  <span key={c} className="text-xs bg-secondary rounded-full px-3 py-1">{c}: <b>{money(v)}</b></span>
                ))}
                {!oneOff.length && <span className="text-sm text-muted-foreground">No expenses in this range.</span>}
              </div>
            </Card>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
                <TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead>
                <TableHead>Receipt</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {oneOff.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No one-off expenses in this range.</TableCell></TableRow>
                ) : oneOff.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDate(e.date)}</TableCell>
                    <TableCell className="text-sm">{e.category}</TableCell>
                    <TableCell className="font-medium text-sm">{e.description || "—"}</TableCell>
                    <TableCell className="text-sm">{e.vendor || "—"}</TableCell>
                    <TableCell className="text-right font-mono-data">{money(e.amount)}</TableCell>
                    <TableCell>{e.receipt_url ? <a href={receiptHref(e.receipt_url)} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 text-xs underline"><Paperclip className="h-3 w-3" />View</a> : "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openDlg(e)} data-testid={`edit-expense-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this expense?") && delMut.mutate(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="recurring">
          <Card className="p-5 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Monthly recurring total</p>
                <p className="text-2xl font-heading font-bold" data-testid="recurring-total">{money(monthlyRecurring)}</p>
              </div>
              <p className="text-xs text-muted-foreground max-w-md text-right">
                Fixed monthly commitments (rent, utilities, insurance, subscriptions). Rolls into Financials as projected overhead.
              </p>
            </div>
          </Card>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Category</TableHead><TableHead>Description</TableHead>
                <TableHead>Vendor</TableHead><TableHead className="text-right">Monthly</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {recurring.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No recurring bills yet. Add rent, utilities, insurance… so they show up in Financials.</TableCell></TableRow>
                ) : recurring.map((e) => (
                  <TableRow key={e.id} data-testid={`recurring-row-${e.id}`}>
                    <TableCell><Badge variant="secondary">{e.category}</Badge></TableCell>
                    <TableCell className="font-medium text-sm">{e.description || "—"}</TableCell>
                    <TableCell className="text-sm">{e.vendor || "—"}</TableCell>
                    <TableCell className="text-right font-mono-data">{money(e.amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openDlg(e)} data-testid={`edit-recurring-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this recurring expense?") && delMut.mutate(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers">
          <Card className="p-5 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Total spent across all suppliers</p>
                <p className="text-2xl font-heading font-bold" data-testid="supplier-total">{money(totalSupplierSpend)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/mechanic/suppliers")} data-testid="manage-suppliers-btn">Manage suppliers</Button>
            </div>
          </Card>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Supplies</TableHead>
                <TableHead className="text-right">Unique parts</TableHead>
                <TableHead className="text-right">Total spent</TableHead>
                <TableHead>Last purchase</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {suppliersOverview.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No supplier spend recorded yet — add supplier to a part on any work order.</TableCell></TableRow>
                ) : suppliersOverview.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/mechanic/suppliers/${s.id}`)} data-testid={`supplier-row-${s.id}`}>
                    <TableCell className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-muted-foreground" />{s.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.supplies || "—"}</TableCell>
                    <TableCell className="text-right">{s.unique_parts}</TableCell>
                    <TableCell className="text-right font-mono-data font-semibold">{money(s.total_spend)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.last_purchase ? fmtDate(s.last_purchase) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit expense" : (form.recurring ? "Add recurring bill" : "Add expense")}</DialogTitle><DialogDescription>{form.recurring ? "Fixed monthly cost — rolls into Financials." : "Attach the receipt photo or PDF."}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {!form.recurring && (
              <div className="space-y-1"><Label className="label-mono">Date</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>
            )}
            <div className={`space-y-1 ${form.recurring ? "col-span-2" : ""}`}>
              <Label className="label-mono">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1"><Label className="label-mono">Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc" /></div>
            <div className="space-y-1"><Label className="label-mono">Vendor</Label><Input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Amount ($) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
            <label className="col-span-2 flex items-center gap-2 border border-border rounded-md p-2 cursor-pointer">
              <Checkbox checked={!!form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: !!v })} data-testid="expense-recurring" />
              <span className="text-sm"><Repeat className="h-3 w-3 inline mr-1" />Recurring monthly bill (rent, utilities, insurance…)</span>
            </label>
            {!form.recurring && (
              <div className="col-span-2 space-y-1">
                <Label className="label-mono">Receipt (photo / PDF)</Label>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center h-9 px-3 rounded-md border border-border text-sm hover:bg-accent">{busy ? "Uploading…" : "Choose file"}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid="expense-receipt-input" />
                  </label>
                  {receipt?.url && <a href={receiptHref(receipt.url)} target="_blank" rel="noreferrer" className="text-sm text-primary underline truncate max-w-[160px]">{receipt.name || "Receipt"}</a>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.amount || saveMut.isPending} data-testid="expense-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
